/**
 * Track Investigation Tab — "I have a suspicious track. Tell me everything."
 *
 * Deep-dive investigation view with track search, metadata, flight profiles
 * (altitude + speed over time), risk assessment, and geofence event timeline.
 */
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  ClockIcon,
  SignalIcon,
  ShieldExclamationIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  PlayIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import clsx from 'clsx';
import { intelligenceApi, type TimeRange } from '../../services/analyticsService';
import { tracksApi } from '../../services/api';
import { RiskGauge, RiskFactorList } from './RiskBadge';
import RiskBadge from './RiskBadge';
import { SkeletonCard, SkeletonChart } from '../common/Skeleton';
import { format, formatDistanceStrict } from 'date-fns';
import toast from 'react-hot-toast';
import { useUnitsStore } from '../../store/unitsStore';
import {
  convertAltitude,
  convertSpeed,
  altitudeUnit,
  speedUnit,
  droneAltLimit,
} from '../../utils/units';

const CLASSIFICATION_COLORS: Record<string, string> = {
  DRONE_SMALL: '#ffb547',
  DRONE_MEDIUM: '#ffce6b',
  DRONE_LARGE: '#ff9f43',
  AIRCRAFT_GENERAL: '#5fd5ff',
  AIRCRAFT_COMMERCIAL: '#5ea3ff',
  HELICOPTER: '#a855f7',
  BIRD: '#c084fc',
  UNKNOWN: '#6b7280',
  CLUTTER: '#e63b4d',
  WEATHER: '#4b5563',
};

/**
 * Prefer a human-readable device label (Manufacturer + Model) over the
 * classification code. Falls back to the classification ("DRONE SMALL")
 * when make/model aren't available, then to a generic "Unknown".
 */
function deviceLabel(t: any): string {
  const parts = [t?.manufacturer, t?.model].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  if (t?.classification) return String(t.classification).replace(/_/g, ' ');
  return 'Unknown';
}

interface InvestigationTabProps {
  tenantId: string;
  timeRange: TimeRange;
  startTime: string;
  endTime: string;
  analyticsAvailable: boolean | null;
  selectedTrackId: string | null;
  onSelectTrack: (trackId: string | null) => void;
}

export default function InvestigationTab({
  tenantId,
  timeRange,
  startTime,
  endTime,
  analyticsAvailable,
  selectedTrackId,
  onSelectTrack,
}: InvestigationTabProps) {
  const navigate = useNavigate();
  const { unitSystem } = useUnitsStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    manufacturer: '',
    model: '',
    serialNumber: '',
    registration: '',
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pre-fill search from selected track
  useEffect(() => {
    if (selectedTrackId) {
      setSearchTerm(selectedTrackId);
      setDebouncedSearch(selectedTrackId);
    }
  }, [selectedTrackId]);

  // Check if any advanced filter is active
  const hasAdvancedFilters = Object.values(advancedFilters).some((v) => v.trim().length > 0);

  // Search tracks
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['tracks-search', debouncedSearch, startTime, endTime, advancedFilters],
    queryFn: async () => {
      const response = await tracksApi.getTracks({
        startTime,
        endTime,
        pageSize: 50,
        // Pass advanced filters to backend for server-side filtering
        ...(advancedFilters.manufacturer && { manufacturer: advancedFilters.manufacturer }),
        ...(advancedFilters.model && { model: advancedFilters.model }),
        ...(advancedFilters.serialNumber && { serialNumber: advancedFilters.serialNumber }),
        ...(advancedFilters.registration && { registration: advancedFilters.registration }),
      });
      const tracks = response.data || [];
      if (!debouncedSearch) return tracks.slice(0, 20);
      const term = debouncedSearch.toLowerCase();
      return tracks.filter(
        (t: any) =>
          t.trackId?.toLowerCase().includes(term) ||
          t.callsign?.toLowerCase().includes(term) ||
          t.icaoAddress?.toLowerCase().includes(term) ||
          t.model?.toLowerCase().includes(term) ||
          t.manufacturer?.toLowerCase().includes(term) ||
          t.serialNumber?.toLowerCase().includes(term) ||
          t.registration?.toLowerCase().includes(term)
      );
    },
    enabled: debouncedSearch.length >= 2 || hasAdvancedFilters || !selectedTrackId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch anomalies for "recent suspects" dropdown
  const { data: anomalies } = useQuery({
    queryKey: ['intelligence', 'anomalies-investigation', tenantId, timeRange],
    queryFn: () => intelligenceApi.getAnomalies(tenantId, startTime, endTime),
    enabled: analyticsAvailable === true && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch selected track detail
  const { data: trackDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['track', selectedTrackId],
    queryFn: () => tracksApi.getTrack(selectedTrackId!),
    enabled: !!selectedTrackId,
    staleTime: 60 * 1000,
  });

  // Fetch track history for flight profiles
  const { data: trackHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['trackHistory', selectedTrackId, startTime, endTime],
    queryFn: () =>
      tracksApi.getTrackHistory(selectedTrackId!, startTime, endTime, 500),
    enabled: !!selectedTrackId,
    staleTime: 60 * 1000,
  });

  // Fetch risk score
  const { data: riskData, isLoading: riskLoading } = useQuery({
    queryKey: ['intelligence', 'risk-investigation', tenantId, selectedTrackId],
    queryFn: () =>
      intelligenceApi.getRiskScore(tenantId, selectedTrackId!),
    enabled: !!selectedTrackId && analyticsAvailable === true && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const track = trackDetail?.data || null;
  const positions = trackHistory?.data?.positions || [];

  // ── Previous Flights — other tracks from the same drone ────────────────
  // Prefer serialNumber (strongest identifier). Fall back to registration.
  // Searches the last 90 days and excludes the currently-selected track.
  const deviceSerial = track?.serialNumber || (track as any)?.metadata?.serialNumber || null;
  const deviceRegistration = track?.registration || null;

  const { data: previousFlightsData, isLoading: previousFlightsLoading } = useQuery({
    queryKey: [
      'track-previous-flights',
      selectedTrackId,
      deviceSerial,
      deviceRegistration,
    ],
    queryFn: async () => {
      const lookbackDays = 90;
      const now = new Date();
      const startLookback = new Date(now.getTime() - lookbackDays * 24 * 60 * 60 * 1000);
      const response = await tracksApi.getTracks({
        startTime: startLookback.toISOString(),
        endTime: now.toISOString(),
        pageSize: 100,
        ...(deviceSerial
          ? { serialNumber: deviceSerial }
          : deviceRegistration
          ? { registration: deviceRegistration }
          : {}),
      });
      return response.data || [];
    },
    enabled: !!selectedTrackId && (!!deviceSerial || !!deviceRegistration),
    staleTime: 5 * 60 * 1000,
  });

  const previousFlights = useMemo(() => {
    if (!previousFlightsData) return [];
    return (previousFlightsData as any[])
      .filter((t) => (t.trackId || t.id) !== selectedTrackId)
      .sort((a, b) => {
        const ta = new Date(a.firstSeen || a.lastSeen || 0).getTime();
        const tb = new Date(b.firstSeen || b.lastSeen || 0).getTime();
        return tb - ta;
      });
  }, [previousFlightsData, selectedTrackId]);

  // Flight profile data — convert from SI storage units to display units
  const altitudeData = useMemo(() => {
    return positions.map((p: any) => ({
      time: format(new Date(p.timestamp), 'HH:mm:ss'),
      altitude: convertAltitude(p.altitudeMSL || 0, unitSystem),
    }));
  }, [positions, unitSystem]);

  const speedData = useMemo(() => {
    return positions.map((p: any) => ({
      time: format(new Date(p.timestamp), 'HH:mm:ss'),
      speed: convertSpeed(p.groundSpeed || 0, unitSystem),
    }));
  }, [positions, unitSystem]);

  // FAA 400ft / ~122m drone altitude limit in the active unit system
  const altLimit = useMemo(() => droneAltLimit(unitSystem), [unitSystem]);

  // Geofence events from track
  const geofenceEvents = useMemo(() => {
    if (!track?.geofenceStates) return [];
    return (track.geofenceStates as any[]).map((gs: any) => ({
      ...gs,
      timestamp: gs.enteredAt || gs.exitedAt || gs.timestamp,
    }));
  }, [track]);

  // Anomalous track suggestions
  const suspects = useMemo(() => {
    if (!anomalies) return [];
    return anomalies
      .filter((a) => a.is_anomaly)
      .sort((a, b) => b.anomaly_score - a.anomaly_score)
      .slice(0, 5);
  }, [anomalies]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Track Selector */}
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-accent" />
          <h3 className="text-sm font-display font-semibold text-white">
            Select Track to Investigate
          </h3>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search input */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value !== selectedTrackId) {
                  onSelectTrack(null);
                }
              }}
              placeholder="Search by Track ID, Callsign, ICAO, Model, or Serial..."
              className="w-full bg-navy-800 border border-navy-600 text-white placeholder-gray-500 rounded-lg px-4 py-2.5 text-sm focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none"
            />
            {searchLoading && (
              <div className="absolute right-3 top-3">
                <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Recent suspects dropdown */}
          {suspects.length > 0 && (
            <div className="flex-shrink-0">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    onSelectTrack(e.target.value);
                    setSearchTerm(e.target.value);
                  }
                }}
                value={selectedTrackId || ''}
                className="bg-navy-800 border border-navy-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-accent outline-none"
              >
                <option value="">Recent Suspects</option>
                {suspects.map((s) => (
                  <option key={s.track_id} value={s.track_id}>
                    {s.track_id.slice(0, 20)}... (score:{' '}
                    {(s.anomaly_score * 100).toFixed(0)}%)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Advanced Filters Toggle */}
        <div className="mt-3">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={clsx(
              'flex items-center gap-1.5 text-xs transition-colors',
              hasAdvancedFilters
                ? 'text-accent font-medium'
                : 'text-gray-500 hover:text-gray-300'
            )}
          >
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
            Advanced Filters
            {hasAdvancedFilters && (
              <span className="bg-accent/20 text-accent text-[10px] px-1.5 py-0.5 rounded-full">
                Active
              </span>
            )}
            {showAdvancedFilters ? (
              <ChevronUpIcon className="h-3 w-3" />
            ) : (
              <ChevronDownIcon className="h-3 w-3" />
            )}
          </button>

          {showAdvancedFilters && (
            <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {([
                { key: 'manufacturer', label: 'Manufacturer', placeholder: 'e.g. DJI, Dronetag' },
                { key: 'model', label: 'Model', placeholder: 'e.g. Mavic 3, BS40' },
                { key: 'serialNumber', label: 'Serial Number', placeholder: 'e.g. 1581F...' },
                { key: 'registration', label: 'Registration', placeholder: 'e.g. N12345' },
              ] as const).map((field) => (
                <div key={field.key}>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    value={advancedFilters[field.key]}
                    onChange={(e) => {
                      setAdvancedFilters((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }));
                      if (selectedTrackId) onSelectTrack(null);
                    }}
                    placeholder={field.placeholder}
                    className="w-full bg-navy-800 border border-navy-600 text-white placeholder-gray-600 rounded-lg px-3 py-2 text-xs focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none"
                  />
                </div>
              ))}
              {hasAdvancedFilters && (
                <div className="col-span-2 lg:col-span-4 flex justify-end">
                  <button
                    onClick={() =>
                      setAdvancedFilters({
                        manufacturer: '',
                        model: '',
                        serialNumber: '',
                        registration: '',
                      })
                    }
                    className="text-xs text-gray-500 hover:text-white transition-colors"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search results */}
        {!selectedTrackId && searchResults && searchResults.length > 0 && (
          <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-navy-700">
            {searchResults.map((track: any) => {
              // Full serial (no truncation) so operators can cross-reference
              // against the USS/FAA registry without having to click in.
              const sn = track.serialNumber ? `SN: ${track.serialNumber}` : null;
              const detectedAt = track.firstSeen || track.lastSeen
                ? format(new Date(track.firstSeen || track.lastSeen), 'MMM d, yyyy · HH:mm')
                : null;
              const badgeLabel = deviceLabel(track);
              const badgeColor =
                CLASSIFICATION_COLORS[track.classification] || '#6b7280';
              // Primary line is the full device label + detected-at time.
              const primary = [badgeLabel, detectedAt].filter(Boolean).join(' · ');

              return (
                <button
                  key={track.trackId || track.id}
                  onClick={() => {
                    const id = track.trackId || track.id;
                    onSelectTrack(id);
                    setSearchTerm(id);
                  }}
                  className="w-full flex items-start justify-between p-3 hover:bg-navy-800 transition-colors text-left border-b border-navy-700 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded"
                        style={{
                          backgroundColor: `${badgeColor}20`,
                          color: badgeColor,
                        }}
                      >
                        {badgeLabel}
                      </span>
                      <span className="text-sm text-white">{primary}</span>
                    </div>
                    {sn && (
                      <div className="mt-1 text-[11px] text-gray-500 font-mono break-all">
                        {sn}
                      </div>
                    )}
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5 ml-2" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* No track selected state */}
      {!selectedTrackId && (
        <div className="glass-card p-12 text-center">
          <MagnifyingGlassIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-display font-semibold text-white mb-2">
            Select a Track to Investigate
          </h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Search for a track above, select from recent suspects, or click
            &ldquo;Investigate&rdquo; on a flagged track from the Threat Analysis tab.
          </p>
        </div>
      )}

      {/* Track Detail */}
      {selectedTrackId && (
        <>
          {/* Track Metadata Card */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-white">
                Track Details
              </h3>
              <div className="flex items-center gap-2">
                {positions.length > 1 && (
                  <button
                    onClick={() => navigate(`/map?replay=${selectedTrackId}`)}
                    className="btn-primary flex items-center gap-1.5 text-sm px-3 py-1.5"
                  >
                    <PlayIcon className="h-4 w-4" />
                    Replay Flight
                  </button>
                )}
                {riskData && <RiskBadge score={riskData.score} size="md" />}
              </div>
            </div>

            {detailLoading ? (
              <SkeletonCard />
            ) : track ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetadataField
                  label="Track ID"
                  value={track.trackId || selectedTrackId}
                  mono
                  copyable
                  onCopy={handleCopy}
                />
                <MetadataField
                  label="Drone"
                  value={deviceLabel(track)}
                  color={CLASSIFICATION_COLORS[track.classification]}
                />
                <MetadataField
                  label="Confidence"
                  value={
                    track.confidence != null
                      ? `${(track.confidence * 100).toFixed(1)}%`
                      : 'N/A'
                  }
                />
                <MetadataField
                  label="Registration"
                  value={track.registration || 'N/A'}
                />
                <MetadataField
                  label="Manufacturer"
                  value={track.manufacturer || 'N/A'}
                />
                <MetadataField
                  label="Model"
                  value={track.model || 'N/A'}
                />
                <MetadataField
                  label="Serial Number"
                  value={track.serialNumber || (track as any).metadata?.serialNumber || 'N/A'}
                  mono
                  copyable
                  onCopy={handleCopy}
                />
                <MetadataField
                  label="Sensor"
                  value={track.sensorType || 'N/A'}
                />
                <MetadataField
                  label="First Seen"
                  value={
                    track.firstSeen
                      ? format(new Date(track.firstSeen), 'MMM d, HH:mm:ss')
                      : 'N/A'
                  }
                />
                <MetadataField
                  label="Last Seen"
                  value={
                    track.lastSeen
                      ? format(new Date(track.lastSeen), 'MMM d, HH:mm:ss')
                      : 'N/A'
                  }
                />
                <MetadataField
                  label="Duration"
                  value={
                    track.firstSeen && track.lastSeen
                      ? formatDistanceStrict(
                          new Date(track.firstSeen),
                          new Date(track.lastSeen)
                        )
                      : 'N/A'
                  }
                />
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400">
                  Track not found. It may have expired or the ID may be incorrect.
                </p>
              </div>
            )}
          </div>

          {/* Previous Flights from Same Drone */}
          {track && (deviceSerial || deviceRegistration) && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-display font-semibold text-white flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-accent" />
                    Previous Flights from This Drone
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Matched by {deviceSerial ? 'serial number' : 'registration'}{' '}
                    · last 90 days
                  </p>
                </div>
                {previousFlights.length > 0 && (
                  <span className="text-xs text-gray-400 bg-navy-800 border border-navy-700 px-2 py-1 rounded">
                    {previousFlights.length} flight{previousFlights.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              {previousFlightsLoading ? (
                <SkeletonCard />
              ) : previousFlights.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-400">
                    This is the first detected flight from this device in the last 90 days.
                  </p>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto rounded-lg border border-navy-700 divide-y divide-navy-700">
                  {previousFlights.map((pf: any) => {
                    const pfId = pf.trackId || pf.id;
                    const first = pf.firstSeen ? new Date(pf.firstSeen) : null;
                    const last = pf.lastSeen ? new Date(pf.lastSeen) : null;
                    const duration =
                      first && last
                        ? formatDistanceStrict(first, last)
                        : null;
                    // Show manufacturer + model on the badge (falls back
                    // to classification if make/model are missing).
                    const badgeLabel = deviceLabel(pf);
                    const badgeColor =
                      CLASSIFICATION_COLORS[pf.classification] || '#6b7280';
                    const riskScore =
                      pf.riskScore != null
                        ? pf.riskScore
                        : pf.risk_score != null
                        ? pf.risk_score
                        : null;

                    return (
                      <div
                        key={pfId}
                        className="flex items-center justify-between gap-3 p-3 hover:bg-navy-800/50 transition-colors"
                      >
                        <button
                          onClick={() => {
                            onSelectTrack(pfId);
                            setSearchTerm(pfId);
                          }}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-white">
                              {first
                                ? format(first, 'MMM d, yyyy · HH:mm')
                                : 'Unknown time'}
                            </span>
                            <span
                              className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded"
                              style={{
                                backgroundColor: `${badgeColor}20`,
                                color: badgeColor,
                              }}
                            >
                              {badgeLabel}
                            </span>
                            {riskScore != null && riskScore > 0 && (
                              <RiskBadge score={riskScore} size="sm" />
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            {duration && (
                              <span className="flex items-center gap-1">
                                <ClockIcon className="h-3 w-3" />
                                {duration}
                              </span>
                            )}
                            <span className="font-mono truncate">
                              {pfId.slice(0, 16)}
                              {pfId.length > 16 ? '…' : ''}
                            </span>
                          </div>
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              onSelectTrack(pfId);
                              setSearchTerm(pfId);
                            }}
                            className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-navy-700 transition-colors"
                            title="Investigate this flight"
                          >
                            Investigate
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/map?replay=${pfId}`);
                            }}
                            className="flex items-center gap-1 text-xs text-accent hover:text-white px-2 py-1 rounded hover:bg-accent/20 transition-colors"
                            title="Replay this flight on the map"
                          >
                            <PlayIcon className="h-3 w-3" />
                            Replay
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* DUMP / Remote ID Extended Metadata */}
          {track && (track as any).metadata && Object.keys((track as any).metadata).length > 0 && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-display font-semibold text-white mb-4">
                Remote ID Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(track as any).metadata?.uasOperatorId && (
                  <MetadataField
                    label="Operator ID"
                    value={(track as any).metadata.uasOperatorId}
                    mono
                    copyable
                    onCopy={handleCopy}
                  />
                )}
                {(track as any).metadata?.manufacturerCode && (
                  <MetadataField
                    label="Manufacturer Code"
                    value={(track as any).metadata.manufacturerCode}
                    mono
                  />
                )}
                {(track as any).metadata?.uaClassification && (
                  <MetadataField
                    label="UA Classification"
                    value={(track as any).metadata.uaClassification}
                  />
                )}
                {(track as any).metadata?.operationalState && (
                  <MetadataField
                    label="Operational State"
                    value={(track as any).metadata.operationalState}
                  />
                )}
                {(track as any).metadata?.operatorLatitude != null && (
                  <MetadataField
                    label="Pilot Location"
                    value={`${((track as any).metadata.operatorLatitude as number).toFixed(6)}, ${((track as any).metadata.operatorLongitude as number).toFixed(6)}`}
                    mono
                    copyable
                    onCopy={handleCopy}
                    href={`https://www.google.com/maps?q=${(track as any).metadata.operatorLatitude},${(track as any).metadata.operatorLongitude}`}
                  />
                )}
                {(track as any).metadata?.operatorAltitudeMSL != null && (
                  <MetadataField
                    label="Pilot Altitude (MSL)"
                    value={`${((track as any).metadata.operatorAltitudeMSL as number).toFixed(1)}m`}
                  />
                )}
                {(track as any).metadata?.protocol && (
                  <MetadataField
                    label="Protocol"
                    value={(track as any).metadata.protocol}
                  />
                )}
              </div>

              {/* UA Identifiers — show all Remote ID identifier types */}
              {(track as any).metadata?.uaIdentifiers && (track as any).metadata.uaIdentifiers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">
                    UA Identifiers
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {((track as any).metadata.uaIdentifiers as Array<{ type: string; value: string }>).map(
                      (id, idx) => (
                        <MetadataField
                          key={idx}
                          label={id.type.replace(/_/g, ' ')}
                          value={id.value}
                          mono
                          copyable
                          onCopy={handleCopy}
                        />
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Flight Descriptions */}
              {(track as any).metadata?.descriptions && (track as any).metadata.descriptions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">
                    Flight Description
                  </h4>
                  <div className="space-y-1">
                    {((track as any).metadata.descriptions as string[]).map((desc, idx) => (
                      <p key={idx} className="text-sm text-gray-300">{desc}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Flight Profile Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Altitude Profile */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPinIcon className="h-5 w-5 text-accent" />
                <h3 className="text-sm font-display font-semibold text-white">
                  Altitude Profile
                </h3>
                <span className="text-xs text-gray-500">MSL ({altitudeUnit(unitSystem)})</span>
              </div>

              {historyLoading ? (
                <SkeletonChart />
              ) : altitudeData.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={altitudeData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#2c4567"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7d97', fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7d97', fontSize: 10 }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#142a4a',
                          border: '1px solid #2c4567',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '12px',
                        }}
                        formatter={(value: number) => [
                          `${value} ${altitudeUnit(unitSystem, 'MSL')}`,
                          'Altitude',
                        ]}
                      />
                      {/* FAA 400ft / ~122m AGL drone altitude limit */}
                      <ReferenceLine
                        y={altLimit.value}
                        stroke="#e63b4d"
                        strokeDasharray="6 4"
                        strokeWidth={1}
                        label={{
                          value: altLimit.label,
                          position: 'insideTopRight',
                          fill: '#e63b4d',
                          fontSize: 10,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="altitude"
                        stroke="#ffb547"
                        strokeWidth={2}
                        dot={false}
                        name="Altitude"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                  No position history available
                </div>
              )}
            </div>

            {/* Speed Profile */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <SignalIcon className="h-5 w-5 text-sky" />
                <h3 className="text-sm font-display font-semibold text-white">
                  Speed Profile
                </h3>
                <span className="text-xs text-gray-500">{speedUnit(unitSystem)}</span>
              </div>

              {historyLoading ? (
                <SkeletonChart />
              ) : speedData.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={speedData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#2c4567"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7d97', fontSize: 10 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7d97', fontSize: 10 }}
                        width={35}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#142a4a',
                          border: '1px solid #2c4567',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '12px',
                        }}
                        formatter={(value: number) => [
                          `${value} ${speedUnit(unitSystem)}`,
                          'Speed',
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="speed"
                        stroke="#5fd5ff"
                        strokeWidth={2}
                        dot={false}
                        name="Ground Speed"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                  No speed data available
                </div>
              )}
            </div>
          </div>

          {/* Risk Assessment + Geofence Timeline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Assessment */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldExclamationIcon className="h-5 w-5 text-red-400" />
                <h3 className="text-sm font-display font-semibold text-white">
                  Risk Assessment
                </h3>
              </div>

              {analyticsAvailable === false ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-400">
                    Analytics service required for risk scoring
                  </p>
                </div>
              ) : riskLoading ? (
                <SkeletonCard />
              ) : riskData ? (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <RiskGauge score={riskData.score} />
                  </div>
                  <RiskFactorList factors={riskData.factors} />
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-400">
                    Unable to calculate risk score for this track
                  </p>
                </div>
              )}
            </div>

            {/* Geofence Event Timeline */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <ClockIcon className="h-5 w-5 text-yellow-400" />
                <h3 className="text-sm font-display font-semibold text-white">
                  Geofence Events
                </h3>
              </div>

              {geofenceEvents.length > 0 ? (
                <div className="space-y-0 relative">
                  {/* Vertical timeline line */}
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-navy-700" />

                  {geofenceEvents.map((event: any, i: number) => {
                    const isEntry =
                      event.state === 'INSIDE' || event.state === 'ENTERING';
                    const isExclusion = event.type === 'EXCLUSION';
                    const dotColor = isEntry
                      ? isExclusion
                        ? 'bg-red-400'
                        : 'bg-yellow-400'
                      : 'bg-accent';

                    return (
                      <div key={i} className="flex items-start gap-3 py-2 relative">
                        <div
                          className={clsx(
                            'w-3.5 h-3.5 rounded-full flex-shrink-0 mt-0.5 ring-2 ring-navy-900 z-10',
                            dotColor
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            {isEntry ? 'Entered' : 'Exited'}{' '}
                            <span className="font-semibold">
                              {event.name || 'Unknown Zone'}
                            </span>
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {event.type && (
                              <span
                                className={clsx(
                                  'text-[10px] px-1.5 py-0.5 rounded',
                                  isExclusion
                                    ? 'bg-red-500/20 text-red-400'
                                    : 'bg-yellow-500/20 text-yellow-400'
                                )}
                              >
                                {event.type}
                              </span>
                            )}
                            <span className="text-[10px] text-gray-500">
                              {event.timestamp
                                ? format(
                                    new Date(event.timestamp),
                                    'HH:mm:ss'
                                  )
                                : 'Unknown time'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <InformationCircleIcon className="h-8 w-8 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">
                    No geofence events recorded for this track
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Events appear when a track enters or exits a defined geofence zone
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Metadata Field Component
// ============================================================================

function MetadataField({
  label,
  value,
  mono,
  color,
  copyable,
  onCopy,
  href,
}: {
  label: string;
  value: string;
  mono?: boolean;
  color?: string;
  copyable?: boolean;
  onCopy?: (text: string) => void;
  href?: string;
}) {
  return (
    <div className="py-2">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <div className="flex items-center gap-1.5">
        {color && (
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
          />
        )}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(
              'text-sm text-accent hover:text-accent/80 underline underline-offset-2 transition-colors',
              // Mono values (serials, IDs) wrap to the next line so the
              // full string is always visible instead of being clipped.
              mono ? 'font-mono text-xs break-all' : 'truncate'
            )}
          >
            {value}
          </a>
        ) : (
          <span
            className={clsx(
              'text-sm text-white',
              mono ? 'font-mono text-xs break-all' : 'truncate'
            )}
          >
            {value}
          </span>
        )}
        {copyable && onCopy && value !== 'N/A' && (
          <button
            onClick={() => onCopy(value)}
            className="flex-shrink-0 text-gray-500 hover:text-accent transition-colors"
          >
            <DocumentDuplicateIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
